import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StatusTransaction } from "enums/orders";
import { ApplicationRootState } from "types";
import { MemberOrderStateType } from "types/orders";

import { extraReducers } from "./orders.extraReducers";

export const initialState: MemberOrderStateType = {
  memberOrders: {
    all: {
      data: null,
      total: 0,
      isLoading: false,
      page: 1,
      pageSize: 10,
      error: null,
      summary: null,
    },
    deposit: {
      data: null,
      total: 0,
      isLoading: false,
      page: 1,
      pageSize: 10,
      error: null,
    },
    withdrawal: {
      data: null,
      total: 0,
      isLoading: false,
      page: 1,
      pageSize: 5,
      error: null,
    },
    rebate: {
      data: null,
      total: 0,
      isLoading: false,
      error: null,
    },
    reward: {
      data: null,
      total: 0,
      isLoading: false,
      page: 1,
      pageSize: 10,
      error: null,
    },
  },
  memberPromotions: {
    data: null,
    total: 0,
    isLoading: false,
    error: null,
  },
  statusTransaction: StatusTransaction.ALL,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    updateStatusTransaction(
      state: MemberOrderStateType,
      { payload }: PayloadAction<StatusTransaction>
    ) {
      state.statusTransaction = payload;
    },
    updateCurrentPageAllHistory(
      state: MemberOrderStateType,
      { payload }: PayloadAction<number>
    ) {
      state.memberOrders.all.page = payload;
    },
    updateCurrentPageDeposit(
      state: MemberOrderStateType,
      { payload }: PayloadAction<number>
    ) {
      state.memberOrders.deposit.page = payload;
    },
    updateCurrentPageWithdrawal(
      state: MemberOrderStateType,
      { payload }: PayloadAction<number>
    ) {
      state.memberOrders.withdrawal.page = payload;
    },
    resetAllHistorytOrders(state: MemberOrderStateType) {
      state.memberOrders.all.total = 0;
      state.memberOrders.all.page = 1;
      state.memberOrders.all.data = null;
      state.memberOrders.all.error = null;
      state.memberOrders.all.summary = null;
    },
    resetDepositOrders(state: MemberOrderStateType) {
      state.memberOrders.deposit.total = 0;
      state.memberOrders.deposit.page = 1;
      state.memberOrders.deposit.data = null;
      state.memberOrders.deposit.error = null;
    },
    resetWithdrawalOrders(state: MemberOrderStateType) {
      state.memberOrders.withdrawal.total = 0;
      state.memberOrders.withdrawal.page = 1;
      state.memberOrders.withdrawal.data = null;
      state.memberOrders.withdrawal.error = null;
    },
    resetRebateOrders(state: MemberOrderStateType) {
      state.memberOrders.rebate.total = 0;
      state.memberOrders.rebate.data = null;
      state.memberOrders.rebate.error = null;
    },
    resetRewardOrders(state: MemberOrderStateType) {
      state.memberOrders.reward.total = 0;
      state.memberOrders.reward.data = null;
      state.memberOrders.reward.error = null;
    },
    resetPromotion(state: MemberOrderStateType) {
      state.memberPromotions.data = null;
      state.memberPromotions.isLoading = false;
      state.memberPromotions.total = 0;
      state.memberPromotions.error = null;
    },
    resetOrders: () => initialState,
  },
  extraReducers,
});

export const selectMemberOrder = (state: ApplicationRootState) =>
  state.orders.memberOrders;

export const selectAllHistoryOrders = (state: ApplicationRootState) =>
  state.orders.memberOrders.all;

export const selectDepositOrders = (state: ApplicationRootState) =>
  state.orders.memberOrders.deposit;

export const selectWithdrawalOrders = (state: ApplicationRootState) =>
  state.orders.memberOrders.withdrawal;

export const selectRebateOrders = (state: ApplicationRootState) =>
  state.orders.memberOrders.rebate;

export const selectMemberPromotions = (state: ApplicationRootState) =>
  state.orders.memberPromotions;

export const selectRewardOrders = (state: ApplicationRootState) =>
  state.orders.memberOrders.reward;

export const selectStatusTransaction = (state: ApplicationRootState) =>
  state.orders.statusTransaction;

export const {
  updateStatusTransaction,
  updateCurrentPageAllHistory,
  updateCurrentPageDeposit,
  updateCurrentPageWithdrawal,
  resetAllHistorytOrders,
  resetDepositOrders,
  resetWithdrawalOrders,
  resetOrders,
  resetRebateOrders,
  resetRewardOrders,
  resetPromotion,
} = ordersSlice.actions;

export default ordersSlice.reducer;
